import { Alert, Pending } from '@cluey/cluey-components';
import classNames from 'classnames';
import orderBy from 'lodash/orderBy';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { api } from '../../../../../api';
import type { Enrolment } from '../../../../../api/types/enrolment';
import { Loader } from '../../../../../components/Loader';
import { useGetSessions } from '../../../../../hooks/queries/useSessionQuery';
import { useGetBulkSessionPolicy } from '../../../../../hooks/queries/useSessionQuery';
import { getTimezone } from '../../../../../selectors/hubSelector';
import { getSessionsOnDateAndBeyond } from '../../../../../selectors/sessionSelector';
import { ENROLMENT_STATUS } from '../../../../../types/hubEnums';
import { getCancelEnrolmentSummaryAlertData } from '../../../../../util/helper/cancelEnrolment';
import { getWillEnrolmentBePaused } from '../../../../../util/helper/enrolment';

interface Props {
  selectedEnrolment: Enrolment;
  selectedDate: string;
  className?: string;
}

function CancelEnrolmentSummaryAlert({ selectedEnrolment, selectedDate, className }: Props) {
  const { enrolmentId, enrolmentStatus, enrolmentPausedFrom, enrolmentPausedTo } = selectedEnrolment;
  const timezone = useSelector(getTimezone);
  const { data: upcomingSessions = [], isLoading: loadingUpcomingSessions } = api.session.all.useQuery('upcoming', {
    select: (data) => {
      const filteredSessions = data.filter((s) => {
        return moment(s.startTime).isSameOrAfter(new Date(), 'day');
      });
      return orderBy(filteredSessions, ['startTime'], ['asc']);
    },
  });
  const pausedOrWillBe =
    enrolmentStatus === ENROLMENT_STATUS.PAUSED ||
    getWillEnrolmentBePaused({
      enrolmentPausedFrom,
      enrolmentPausedTo,
    });

  const { data: pausedSessions, isLoading: isPausedSessionsLoading } = useGetSessions({
    filter: 'paused',
    enabled: pausedOrWillBe,
  });

  const sessionsOnOrAfterCancellationDate = useSelector(
    getSessionsOnDateAndBeyond({
      date: selectedDate,
      enrolmentId,
      timezone,
      additionalSessions: pausedSessions ?? [],
    })
  );

  const {
    data: bulkSessionPolicy,
    isLoading: bulkSessionPolicyLoading,
    error: bulkPolicyError,
  } = useGetBulkSessionPolicy({
    sessionIds: sessionsOnOrAfterCancellationDate.map((session) => session.studentSessionId),
    enabled: sessionsOnOrAfterCancellationDate.length > 0,
  });

  if (bulkPolicyError) {
    return (
      <Alert body="" type="error">
        There was an error retrieving the enrolment cancellation details.
      </Alert>
    );
  }

  const { alertType, body } = getCancelEnrolmentSummaryAlertData({
    bulkSessionPolicy,
    enrolmentId,
    upcomingSessions,
    timezone,
    selectedDate,
  });

  return (
    <Pending
      loading={
        (bulkSessionPolicyLoading && sessionsOnOrAfterCancellationDate.length > 0) ||
        (isPausedSessionsLoading && pausedOrWillBe) ||
        loadingUpcomingSessions
      }
      loader={<Loader />}
    >
      <Alert className={classNames('!my-7', className)} type={alertType} title={null} body={null}>
        {body}
      </Alert>
    </Pending>
  );
}

export default CancelEnrolmentSummaryAlert;
